import { throttle } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

interface UseAudioElementProps {
  src: string | null;
  startTime?: number;
  endTime?: number;
  loop?: boolean;
  onDurationChange?: (duration: number) => void;
  onCurrentTimeChange?: (currentTime: number) => void;
  onIsPlayingChange?: (isPlaying: boolean) => void;
  volume?: number;
}

/**
 * Hook to manage the audio element and its state
 */
export const useAudioElement = ({
  src,
  startTime,
  endTime,
  loop,
  onDurationChange,
  onCurrentTimeChange,
  onIsPlayingChange,
  volume,
}: UseAudioElementProps) => {
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [isPlaying, setIsPlayingState] = useState(false);
  const [currentTime, setCurrentTimeState] = useState(0);

  const setPlayState = useCallback(
    (newState: 'play' | 'pause') => {
      if (newState === 'play') {
        audioElement?.play();
      } else {
        audioElement?.pause();
      }
    },
    [audioElement]
  );

  const internalSetCurrentTime = useCallback(
    (newTime: number) => {
      if (!audioElement) return;
      setCurrentTimeState(newTime);
      onCurrentTimeChange?.(newTime);
    },
    [audioElement, onCurrentTimeChange]
  );

  const setCurrentTime = useCallback(
    (newTime: number) => {
      if (!audioElement) return;
      audioElement.currentTime = newTime;
      internalSetCurrentTime(newTime);
    },
    [audioElement, internalSetCurrentTime]
  );

  useEffect(() => {
    if (audioElement) {
      audioElement.volume = volume ?? 1;
    }
  }, [volume, audioElement]);

  // load the audio element, and reset the duration and audio element when the src changes
  useEffect(() => {
    if (src) {
      const audio = new Audio(src);
      setAudioElement(audio);
      audio.onloadeddata = () => {
        const audioDuration = audio.duration;
        if (typeof audioDuration === 'number' && !isNaN(audioDuration)) {
          setDuration(audioDuration);
          onDurationChange?.(audioDuration);
        }
      };

      // should not be needed, but just in case
      audio.ondurationchange = () => {
        const audioDuration = audio.duration;
        if (typeof audioDuration === 'number' && !isNaN(audioDuration)) {
          setDuration(audioDuration);
          onDurationChange?.(audioDuration);
        }
      };
      return () => {
        audio.pause();
        audio.src = '';
        setAudioElement(null);
        setDuration(null);
      };
    }
  }, [src, onDurationChange]);

  const updatePlayingState = useCallback(
    (nextState: boolean) => {
      setIsPlayingState(nextState);
      onIsPlayingChange?.(nextState);
    },
    [onIsPlayingChange]
  );

  // make sure the audio element is set up correctly
  // and register the relevant event listeners
  useEffect(() => {
    if (!audioElement) return;
    if (startTime) {
      internalSetCurrentTime(startTime);
      audioElement.currentTime = startTime;
    }

    audioElement.onplay = () => {
      updatePlayingState(true);
    };
    audioElement.onpause = () => {
      updatePlayingState(false);
    };
    audioElement.onended = () => {
      // Loop back to start time
      const loopStartTime = startTime ?? 0;
      audioElement.currentTime = loopStartTime;
      internalSetCurrentTime(loopStartTime);
      if (loop) {
        audioElement.play();
      } else {
        updatePlayingState(false);
      }
    };
    audioElement.ontimeupdate = throttle(() => {
      const audioCurrentTime = audioElement.currentTime;

      // Handle reaching the end time
      if (endTime && audioCurrentTime >= endTime) {
        if (!loop) {
          audioElement.pause();
          updatePlayingState(false);
        }
        // Loop back to start time and continue playing
        const loopStartTime = startTime ?? 0;
        audioElement.currentTime = loopStartTime;
        internalSetCurrentTime(loopStartTime);
      } else {
        // Update the current time as usual
        internalSetCurrentTime(audioCurrentTime);
      }
    }, 100);
  }, [audioElement, onCurrentTimeChange, updatePlayingState, internalSetCurrentTime, startTime, loop, endTime]);

  return { duration, setPlayState, isPlaying, currentTime, setCurrentTime, audioElement };
};
